import "./PortalHeading.scss";

import clsx from "clsx";
import { ReactNode } from "react";
import { FCC } from "types";

type PortalHeadingVariant = "default" | "cockpit";

const variantClassName: Record<PortalHeadingVariant, string> = {
  default: "",
  // Cockpit: the big DS greeting, flush with the content zone - the default
  // still carries the pre-redesign heading size and its own side padding.
  cockpit: "is-cockpit",
};

export const PortalHeading: FCC<{
  isLoading?: boolean;
  heading: string;
  icon?: ReactNode;
  variant?: PortalHeadingVariant;
  /**
   * Control that belongs to the headline itself (e.g. "Abo hinzufügen") and
   * therefore sits inside the headline column, directly below the title -
   * unlike `children`, which is appended after the whole heading block.
   */
  action?: ReactNode;
}> = ({ heading, isLoading, icon, action, variant = "default", children }) => {
  const content = (
    <>
      {/* Mobile stacks the icon tile above the headline and pushes it to the
          right edge; tablet/desktop put the two side by side, separated by the
          `gutter` token. */}
      <div className="flex flex-col gap-gutter tablet:flex-row tablet:items-center">
        {icon && (
          <div className="flex size-24 shrink-0 items-center justify-center self-end rounded-lg bg-background-primary tablet:self-auto">
            {icon}
          </div>
        )}
        <div className="flex min-w-0 flex-col gap-4">
          <span className="portal-title">{heading}</span>
          {action}
        </div>
      </div>
      {children}
    </>
  );

  return (
    <div
      id="portal-heading"
      data-testid="portal-heading"
      className={clsx(
        variantClassName[variant],
        isLoading && "isLoading",
        icon && "has-card",
      )}
    >
      {/* Pages with a header icon (e.g. Documents) wrap the whole
          heading block in its own ivory card, matching the section cards
          below it — pages without an icon keep the plain bare heading. */}
      {icon ? (
        <div className="rounded-lg bg-surface-accent-ivory p-gutter">
          {content}
        </div>
      ) : (
        content
      )}
    </div>
  );
};
